import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router';
import { auth, MfaRequiredError, setAuthToken, setRefreshToken } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Alert } from '@rankhelm/ui';

export function MagicLinkVerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, setAccount } = useAuth();
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    if (!token || !email) {
      setError('Invalid magic link. Please request a new one.');
      setVerifying(false);
      return;
    }

    let cancelled = false;

    async function verify() {
      try {
        const res = await auth.verifyMagicLink({ token: token!, email: email! });

        if (cancelled) return;

        if (res.data.mfa_required && res.data.mfa_token) {
          throw new MfaRequiredError(res.data.mfa_token);
        }

        setAuthToken(res.data.token ?? null);
        setRefreshToken(res.data.refresh_token ?? null);
        setUser(res.data.user ?? null);
        setAccount(res.data.account ?? null);
        navigate('/dashboard', { replace: true });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof MfaRequiredError) {
          // Store MFA token and redirect to login for MFA challenge
          sessionStorage.setItem('rankhelm_mfa_token', err.mfaToken);
          navigate('/login?mfa=true', { replace: true });
          return;
        }
        setError(err instanceof Error ? err.message : 'Magic link verification failed');
      } finally {
        if (!cancelled) setVerifying(false);
      }
    }

    verify();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, setUser, setAccount]);

  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo-full.png" alt="RankHelm" className="h-10 mx-auto" />
        </div>

        <div className="bg-navy-900 border border-navy-700 rounded-xl p-8 text-center">
          {verifying && (
            <>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-helm-400 mx-auto mb-4" />
              <p className="text-slate-300">Verifying your magic link...</p>
            </>
          )}

          {!verifying && error && (
            <>
              <Alert variant="error" className="mb-4">{error}</Alert>
              <Link
                to="/login"
                className="text-helm-400 hover:text-helm-300 transition-colors text-sm"
              >
                Back to login
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
